import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, X, Image, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import ImageUpload from '../../ImageUpload';
import { API, axios } from './adminConfig';

const HomePageTab = ({ homePageContent, fetchHomePageContent }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [imageLibraryImages, setImageLibraryImages] = useState([]);
  const [currentImageField, setCurrentImageField] = useState(null);

  useEffect(() => {
    if (homePageContent && Object.keys(homePageContent).length > 0 && !initialized) {
      setFormData(homePageContent);
      setInitialized(true);
    }
  }, [homePageContent, initialized]);

  const searchImageLibrary = async (search = '', fileType = 'image') => {
    try {
      const params = { public_access: "true", file_type: fileType };
      if (search) params.search = search;
      
      const response = await axios.get(`${API}/admin/image-library`, { params });
      
      const transformedImages = response.data.map(image => ({
        id: image.id,
        urls: {
          raw: image.url,
          regular: image.url,
          small: image.url
        },
        alt_description: image.name,
        description: image.description,
        tags: image.tags || []
      }));
      
      setImageLibraryImages(transformedImages);
    } catch (error) {
      console.error('Image Library API error:', error);
      setImageLibraryImages([]);
      toast.error('Error loading image library');
    }
  };

  const openImageLibrary = (fieldName) => {
    setCurrentImageField(fieldName);
    setShowImageLibrary(true);
    searchImageLibrary();
  };

  const selectImageFromLibrary = (imageUrl) => {
    if (currentImageField) {
      handleInputChange(currentImageField, imageUrl);
    }
    setShowImageLibrary(false);
    setCurrentImageField(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/home-content`, formData);
      toast.success("Home page updated successfully!");
      setInitialized(false);
      await fetchHomePageContent();
    } catch (error) {
      toast.error("Error updating home page");
      console.error('Error updating home page:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({...prev, [field]: value}));
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-burgundy font-serif">Home Page Editor</h2>
      
      <Card className="bg-white border-burgundy/20">
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Edit Home Page Content</CardTitle>
          <CardDescription>Update the text and images on your landing page</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Hero Section */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-burgundy font-serif">Hero Section</h3>
              <div className="bg-cream/30 p-4 rounded-lg border border-burgundy/20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Hero Title</label>
                    <Input
                      value={formData.hero_title || ''}
                      onChange={(e) => handleInputChange('hero_title', e.target.value)}
                      className="border-burgundy/30 focus:border-gold"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Hero Subtitle</label>
                    <Input
                      value={formData.hero_subtitle || ''}
                      onChange={(e) => handleInputChange('hero_subtitle', e.target.value)}
                      className="border-burgundy/30 focus:border-gold"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-burgundy mb-2">Hero Description</label>
                  <Textarea
                    value={formData.hero_description || ''}
                    onChange={(e) => handleInputChange('hero_description', e.target.value)}
                    className="border-burgundy/30 focus:border-gold"
                    rows={3}
                  />
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">CTA Button Text</label>
                    <Input
                      value={formData.hero_cta_text || ''}
                      onChange={(e) => handleInputChange('hero_cta_text', e.target.value)}
                      className="border-burgundy/30 focus:border-gold"
                    />
                  </div>
                  <div>
                    <ImageUpload
                      imageType="hero"
                      currentImageUrl={formData.hero_image_url}
                      onImageUploaded={(url) => handleInputChange('hero_image_url', url)}
                      label="Hero Image"
                      description="Upload hero background image"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* About Section */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-burgundy font-serif">About Section</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">About Title</label>
                  <Input
                    value={formData.about_title || ''}
                    onChange={(e) => handleInputChange('about_title', e.target.value)}
                    className="border-burgundy/30 focus:border-gold"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">About Subtitle</label>
                  <Input
                    value={formData.about_subtitle || ''}
                    onChange={(e) => handleInputChange('about_subtitle', e.target.value)}
                    className="border-burgundy/30 focus:border-gold"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">About Description</label>
                <Textarea
                  value={formData.about_description || ''}
                  onChange={(e) => handleInputChange('about_description', e.target.value)}
                  className="border-burgundy/30 focus:border-gold"
                  rows={3}
                />
              </div>
            </div>

            {/* Pillars Section */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-burgundy font-serif">Three Pillars Section</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map(num => (
                  <Card key={num} className="p-4 border-burgundy/20">
                    <h4 className="font-medium text-burgundy mb-3">Pillar {num}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-burgundy mb-2">Title</label>
                        <Input
                          value={formData[`pillar${num}_title`] || ''}
                          onChange={(e) => handleInputChange(`pillar${num}_title`, e.target.value)}
                          className="border-burgundy/30 focus:border-gold"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-burgundy mb-2">Image</label>
                        {formData[`pillar${num}_image`] && (
                          <div className="mb-2">
                            <img 
                              src={formData[`pillar${num}_image`]} 
                              alt={`Pillar ${num}`} 
                              className="w-full h-32 object-cover rounded border border-burgundy/20"
                            />
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => openImageLibrary(`pillar${num}_image`)}
                          className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                        >
                          <Image className="h-4 w-4 mr-2" />
                          Choose from Image Library
                        </Button>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-burgundy mb-2">Description</label>
                      <Textarea
                        value={formData[`pillar${num}_description`] || ''}
                        onChange={(e) => handleInputChange(`pillar${num}_description`, e.target.value)}
                        className="border-burgundy/30 focus:border-gold"
                        rows={2}
                      />
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Testimonial */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-burgundy font-serif">Testimonial Section</h3>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.show_testimonial || false}
                    onChange={(e) => handleInputChange('show_testimonial', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-burgundy">Show testimonial section</span>
                </label>
              </div>
              <div className={formData.show_testimonial ? '' : 'opacity-50'}>
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Testimonial Text</label>
                  <Textarea
                    value={formData.testimonial_text || ''}
                    onChange={(e) => handleInputChange('testimonial_text', e.target.value)}
                    className="border-burgundy/30 focus:border-gold"
                    rows={3}
                    disabled={!formData.show_testimonial}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Author Name</label>
                    <Input
                      value={formData.testimonial_author || ''}
                      onChange={(e) => handleInputChange('testimonial_author', e.target.value)}
                      className="border-burgundy/30 focus:border-gold"
                      disabled={!formData.show_testimonial}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Author Title</label>
                    <Input
                      value={formData.testimonial_title || ''}
                      onChange={(e) => handleInputChange('testimonial_title', e.target.value)}
                      className="border-burgundy/30 focus:border-gold"
                      disabled={!formData.show_testimonial}
                    />
                  </div>
                  <div>
                    <ImageUpload
                      imageType="testimonial"
                      currentImageUrl={formData.testimonial_image}
                      onImageUploaded={(url) => handleInputChange('testimonial_image', url)}
                      label="Author Photo"
                      description="Upload testimonial author photo"
                      disabled={!formData.show_testimonial}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={loading} className="bg-burgundy hover:bg-burgundy/80 px-8">
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Image Library Modal */}
      {showImageLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 10001 }}>
          <Card className="w-full max-w-6xl mx-4 max-h-[90vh] overflow-auto relative" style={{ zIndex: 10002 }}>
            <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-white">
              <CardTitle className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-serif">Choose Image from Library</h3>
                  <p className="text-sm text-white/80">Select an image from your uploaded library</p>
                </div>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowImageLibrary(false)} 
                  className="text-white hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {imageLibraryImages.map((image) => (
                  <div
                    key={image.id}
                    className="cursor-pointer group relative overflow-hidden rounded-lg border-2 border-transparent hover:border-gold transition-all"
                    onClick={() => selectImageFromLibrary(image.urls.regular)}
                  >
                    <img
                      src={image.urls.small}
                      alt={image.alt_description}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                      <CheckCircle className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    {image.alt_description && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <p className="text-white text-xs truncate">{image.alt_description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {imageLibraryImages.length === 0 && (
                <div className="text-center py-12">
                  <Image className="h-16 w-16 mx-auto text-burgundy/30 mb-4" />
                  <p className="text-burgundy/60 mb-2">No images in your library yet</p>
                  <p className="text-sm text-burgundy/40">Go to the Image Library tab to upload images first</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export { HomePageTab };
